import React, { useEffect, useState } from "react";
import { useSearchParams, Link } from "react-router-dom";
import { api, errText } from "../lib/api";
import { toast } from "sonner";
import { Heart, ArrowLeft, Trophy } from "lucide-react";

const Compare = () => {
  const [params] = useSearchParams();
  const idsParam = params.get("ids") || "";
  const [tryons, setTryons] = useState(null);
  const [err, setErr] = useState(null);

  useEffect(() => {
    let ignore = false;
    api.get(`/compare?ids=${encodeURIComponent(idsParam)}`)
      .then((r) => { if (!ignore) setTryons(r.data); })
      .catch((e) => { if (!ignore) setErr(errText(e, "Could not load fittings")); });
    return () => { ignore = true; };
    // Only re-run when the URL ids change; `api`/`errText`/`encodeURIComponent`
    // are module-level stable references (not React state), so exhaustive-deps
    // does not apply.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [idsParam]);

  const pickWinner = async (t) => {
    const candidateIds = (tryons || []).map((x) => x.id);
    try {
      const r = await api.post("/compare/pick-winner", {
        winner_id: t.id,
        candidate_ids: candidateIds,
      });
      setTryons(r.data);
      toast.success("Winner picked");
    } catch (e) {
      toast.error(errText(e, "Could not pick winner"));
    }
  };

  if (err) {
    return (
      <div className="max-w-3xl mx-auto px-6 py-16 text-center">
        <p className="font-serif-display text-3xl mb-4">Compare didn't load</p>
        <p className="text-black/60">{err}</p>
        <Link to="/outfits" className="btn-outline-ink mt-8 inline-flex items-center gap-2">
          <ArrowLeft className="w-3.5 h-3.5"/> Back to archive
        </Link>
      </div>
    );
  }

  if (!tryons) {
    return (
      <div className="max-w-3xl mx-auto px-6 py-16 text-center font-mono-eyebrow text-black/50">
        Loading the two fittings…
      </div>
    );
  }

  const gridCols = tryons.length === 2 ? "grid-cols-1 md:grid-cols-2" : "grid-cols-2 md:grid-cols-3";

  return (
    <div data-testid="compare-page" className="max-w-[1400px] mx-auto px-6 sm:px-10 py-10">
      <div className="border-b border-black/10 pb-8 mb-10 flex items-end justify-between gap-4 flex-wrap">
        <div>
          <div className="font-mono-eyebrow">Head-to-head</div>
          <h1 className="font-serif-display text-5xl md:text-6xl leading-none mt-2">Pick the winner.</h1>
          <p className="text-black/60 mt-3 max-w-xl">Sit with each fitting. Then choose the one that feels most like you. Winners get a mark on the Archive.</p>
        </div>
        <Link to="/outfits" className="btn-outline-ink inline-flex items-center gap-2">
          <ArrowLeft className="w-3.5 h-3.5"/> Back to archive
        </Link>
      </div>

      <div className={`grid ${gridCols} gap-6`}>
        {tryons.map((t, i) => (
          <div
            key={t.id}
            data-testid={`compare-tile-${i}`}
            className={`bg-white border relative overflow-hidden transition-shadow ${
              t.is_favorite ? "border-[color:var(--accent)] ring-2 ring-[color:var(--accent)] shadow-lg" : "border-black/10"
            }`}
          >
            <div className="aspect-[3/4] overflow-hidden bg-neutral-100 relative">
              <img src={t.result_data_url} alt={`Option ${String.fromCharCode(65 + i)}`} className="w-full h-full object-cover"/>
              <span className="absolute top-4 left-4 bg-white/90 backdrop-blur px-3 py-1 font-mono-eyebrow">
                Option {String.fromCharCode(65 + i)}
              </span>
              {t.is_favorite && (
                <span
                  data-testid={`compare-winner-badge-${i}`}
                  className="absolute top-4 right-4 bg-black text-white px-3 py-1 font-mono-eyebrow inline-flex items-center gap-1.5"
                >
                  <Trophy className="w-3 h-3"/> Winner
                </span>
              )}
            </div>
            <div className="p-6 flex items-center justify-between gap-4">
              <div>
                <div className="font-mono-eyebrow text-black/50">
                  {t.pose || "front"} · {(t.background || "studio").replace("-", " ")}
                </div>
                <div className="font-serif-display text-xl leading-tight mt-1">
                  Fitting {String.fromCharCode(65 + i)}
                </div>
              </div>
              <button
                data-testid={`compare-pick-btn-${i}`}
                onClick={() => pickWinner(t)}
                disabled={t.is_favorite}
                className={`inline-flex items-center gap-2 px-4 py-2 font-mono-eyebrow transition-colors ${
                  t.is_favorite
                    ? "bg-black text-white cursor-default"
                    : "border border-black hover:bg-black hover:text-white"
                }`}
              >
                <Heart className={`w-3.5 h-3.5 ${t.is_favorite ? "fill-white" : ""}`}/>
                {t.is_favorite ? "Picked" : "Pick as winner"}
              </button>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
};

export default Compare;
